/**
 * 71.8 (DECISIONS 369–375): the ground's look, its GLSL — ported from the surface sheet (design_sheets/surface_strata.ts,
 * rounds 2–4), trimmed to the looks and marks the picks reach: D (strokes, level 5), E (painted tiles, level 6) and the
 * round-3 marks (level 8: H and R), each over A (the relief light). A world compiles only its own materials (SL_M<n>) and
 * marks (SL_K<id>, SL_MK); rendering/surfaceLook.ts sets the defines and chains it onto the planet's mesh and the patch.
 */

/** The hashes and value noise (one source for every take). */
export const SL_NOISE_GLSL = /* glsl */`
float slHash(vec3 p) { p = fract(p * 0.1031); p += dot(p, p.zyx + 31.32); return fract((p.x + p.y) * p.z); }
vec3 slHash3(vec3 p) { p = fract(p * vec3(0.1031, 0.1030, 0.0973)); p += dot(p, p.yxz + 33.33); return fract((p.xxy + p.yxx) * p.zyx); }
float slNoise(vec3 x) {
  vec3 i = floor(x), f = fract(x), u = f * f * (3.0 - 2.0 * f);
  return mix(mix(mix(slHash(i), slHash(i + vec3(1, 0, 0)), u.x), mix(slHash(i + vec3(0, 1, 0)), slHash(i + vec3(1, 1, 0)), u.x), u.y),
             mix(mix(slHash(i + vec3(0, 0, 1)), slHash(i + vec3(1, 0, 1)), u.x), mix(slHash(i + vec3(0, 1, 1)), slHash(i + vec3(1, 1, 1)), u.x), u.y), u.z);
}
float slFbm(vec3 x) { return 0.62 * slNoise(x) + 0.38 * slNoise(x * 2.07 + 5.3); }
/** a feature of wavelength w (people) kept while it spans a few pixels at this footprint (people a pixel), faded before it aliases */
float slKeep(float w, float px) { return 1.0 - smoothstep(w * 0.12, w * 0.4, px); }`;

/** The takes: the fields, D's strokes, E's tiles on a hex tiling, round 3's marks, the triplanar frame, the rules' tint. */
export const SL_TAKES_GLSL = /* glsl */`
uniform float uRules;
uniform vec3 uSunW;
uniform vec2 uAlt;
uniform sampler2DArray uTiles;
uniform float uLSize[SL_LAYERS];
uniform float uLMean[SL_LAYERS];
uniform float uLIdx[SL_LAYERS];
varying vec3 vCol;
varying vec4 vW0;
varying vec4 vW1;
/** where we are: the point (people, from the centre), its derivatives, a pixel's footprint (people), the ground's slope and
 *  curvature, the fields (the wind, downhill, the level line, the grass's lean), and the rules' reads */
struct SlCtx { vec3 p; vec3 up; vec3 dpx; vec3 dpy; float px; float slope; float cav; vec3 wind; vec3 down; vec3 contour; vec3 lean; float alt; float moist; float aspect; float dry; float on; vec3 view; };
vec2 slPerp(vec2 d) { return vec2(-d.y, d.x); }
vec2 slRot(vec2 d, float a) { float c = cos(a), s = sin(a); return vec2(c * d.x - s * d.y, s * d.x + c * d.y); }
/** noise stretched: sc.x along d, sc.y across it */
float slAn(vec2 uv, vec2 d, vec2 sc, float seed) { return slNoise(vec3(dot(uv, d) * sc.x, dot(uv, slPerp(d)) * sc.y, seed)); }

/** F, and the clumps and patches under D and E: each material as a field — no marks, no cells; near (its fibre, ripple or
 *  crack, × nearAmt), mid (clumps, drifts: the rules' distance ladder) and far (patches) */
vec3 slField(int m, SlCtx x, vec2 uv, vec2 wind, vec2 down, vec2 lean, float nearAmt, out float h) {
  float kM = slKeep(3.0, x.px) * x.on, kF = slKeep(30.0, x.px) * x.on;
  float g = 1.0; vec3 tint = vec3(1.0); h = 0.0;
  if (false) {}
#ifdef SL_M0
  else if (m == 0) {          // grass: fibres along the lean, tufts, meadow patches
    float fib = slAn(uv, wind, vec2(1.4, 6.0), 1.0) * slAn(uv, wind, vec2(0.9, 2.6), 1.7) * slKeep(0.5, x.px) * 2.0;
    float tuft = slFbm(vec3(uv / 2.6, 2.0)), mead = slFbm(vec3(uv / 24.0, 3.0));
    g = 1.0 + 0.26 * (fib - 0.5) * nearAmt + 0.3 * (tuft - 0.5) * kM + 0.36 * (mead - 0.5) * kF;
    tint = mix(vec3(1.0), vec3(1.07, 1.04, 0.84), smoothstep(0.55, 0.8, mead) * kF);
    h = fib * 0.4 * nearAmt + tuft * 0.5;
  }
#endif
#ifdef SL_M1
  else if (m == 1) {   // rock: beds along the level lines (a ledge where it steepens), fractured planes, cracks
    float bed = fract(length(x.p) / 1.6 + 0.9 * slNoise(x.p / 11.0));
    float ledge = smoothstep(0.72, 0.9, bed) * smoothstep(0.1, 0.35, x.slope) * slKeep(1.6, x.px);
    float pl = floor(slFbm(vec3(uv * 0.28, 4.0)) * 6.0) / 6.0;
    float crack = smoothstep(0.93, 0.985, 1.0 - abs(2.0 * slNoise(vec3(uv * 0.33, 5.0)) - 1.0)) * slKeep(0.6, x.px);
    float blot = slFbm(vec3(uv / 28.0, 6.0));
    g = (0.86 + 0.28 * pl * slKeep(3.0, x.px)) * (1.0 - 0.3 * crack * nearAmt) * (1.0 - 0.22 * ledge) * (1.0 + 0.32 * (blot - 0.5) * kF);
    h = pl * 0.7 + 0.3 * ledge - crack * 0.4 * nearAmt;
  }
#endif
#ifdef SL_M2
  else if (m == 2) {   // sand: ripples across the wind (a gentle windward face, a steep lee), drifts, patches
    float a = dot(uv, wind) * 2.6 + 2.2 * slNoise(vec3(uv * 0.3, 7.0));
    float f = fract(a), prof = f < 0.78 ? f / 0.78 : (1.0 - f) / 0.22;
    float amp = (0.55 + 0.45 * smoothstep(0.25, 0.6, slNoise(vec3(uv * 0.5, 8.0)))) * slKeep(0.4, x.px) * nearAmt
      * (x.on > 0.5 ? 1.0 - smoothstep(0.2, 0.45, x.slope) : 1.0);
    float drift = slAn(uv, wind, vec2(0.06, 0.18), 9.0), far = slFbm(vec3(uv / 40.0, 10.0));
    g = 1.0 + 0.22 * (prof - 0.5) * amp + 0.2 * (drift - 0.5) * kM + 0.22 * (far - 0.5) * kF;
    h = prof * 0.35 * amp + drift * 0.8;
  }
#endif
#ifdef SL_M3
  else if (m == 3) {   // snow: broad drifts along the wind, scoured streaks on the exposed ground
    float drift = slAn(uv, wind, vec2(0.05, 0.18), 11.0);
    float expo = x.on > 0.5 ? clamp(0.35 - 0.5 * x.cav + 0.5 * max(x.alt, 0.0), 0.0, 1.0) : 0.4;
    float scour = smoothstep(0.55, 0.8, slAn(uv, wind, vec2(0.3, 5.0), 12.0)) * expo * slKeep(0.4, x.px) * nearAmt;
    g = 1.0 + 0.08 * (drift - 0.5) * (0.4 + kM);
    tint = mix(vec3(1.0), vec3(0.88, 0.93, 1.04), scour) * mix(vec3(1.0), vec3(0.95, 0.97, 1.03), (1.0 - drift) * (0.4 + kM));
    h = drift * 1.2 - scour * 0.15;
  }
#endif
#ifdef SL_M4
  else if (m == 4) {   // ice: streaks along the wind, white cracks, clear patches
    float st = slAn(uv, wind, vec2(0.12, 3.0), 13.0);
    float crack = smoothstep(0.95, 0.99, 1.0 - abs(2.0 * slNoise(vec3(uv * 0.25, 14.0)) - 1.0)) * slKeep(0.5, x.px) * nearAmt;
    float clear = smoothstep(0.5, 0.7, slFbm(vec3(uv / 9.0, 15.0)));
    g = (0.92 + 0.16 * st) * (1.0 + 0.25 * crack);
    tint = mix(vec3(1.0), vec3(0.86, 0.95, 1.1), clear * 0.7);
    h = st * 0.1 + crack * 0.1;
  }
#endif
#ifdef SL_M5
  else if (m == 5) {   // mud: puddles (more where it is wet), ruts down the fall line
    float wetN = slFbm(vec3(uv / 3.4, 16.0)) + (x.on > 0.5 ? (x.moist - 0.5) * 0.35 : 0.0);
    float wet = mix(0.15, smoothstep(0.56, 0.6, wetN), slKeep(3.4, x.px));   // (faded to its share past a few people: a hard edge flecked the diorama black)
    float rut = slAn(uv, wind, vec2(0.25, 3.2), 17.0) * slKeep(0.6, x.px);
    g = (1.0 - 0.35 * wet) * (1.0 + 0.16 * (rut - 0.5) * nearAmt) * (1.0 + 0.2 * (slFbm(vec3(uv / 20.0, 18.0)) - 0.5) * kF);
    tint = mix(vec3(1.0), vec3(0.85, 0.92, 1.05), wet);
    h = (1.0 - wet) * (0.3 + 0.4 * rut);
  }
#endif
#ifdef SL_M6
  else if (m == 6) {   // ash: a fine grain, soft drifts along the wind
    float grain = slNoise(vec3(uv * 5.0, 19.0)) * slKeep(0.4, x.px);
    float drift = slAn(uv, wind, vec2(0.08, 0.3), 20.0), far = slFbm(vec3(uv / 30.0, 21.0));
    g = 1.0 + 0.16 * (grain - 0.5) * nearAmt + 0.2 * (drift - 0.5) * kM + 0.2 * (far - 0.5) * kF;
    h = drift * 0.6 + grain * 0.08;
  }
#endif
#ifdef SL_M7
  else if (m == 7) {     // salt: pressure ridges in a network (ridged noise: no cells)
    float r1 = 1.0 - abs(2.0 * slNoise(vec3(uv * 0.55, 22.0)) - 1.0), r2 = 1.0 - abs(2.0 * slNoise(vec3(uv * 1.4, 23.0)) - 1.0);
    float ridge = max(smoothstep(0.9, 0.97, r1), 0.6 * smoothstep(0.92, 0.98, r2)) * slKeep(0.5, x.px) * nearAmt;
    g = (0.96 + 0.08 * slFbm(vec3(uv / 6.0, 24.0))) * (1.0 + 0.12 * ridge);
    h = ridge * 0.5;
  }
#endif
  return g * tint;
}

/** D's brush: one layer of strokes on unit cells — each cell's stroke (if its hash is under dens) from a jittered centre along
 *  d (± jit), L long and W wide (cells), tapered toward its tip; the topmost by a hashed order (the same from every cell, so no
 *  seam): x its cover, y its id, z how far along it (0 the base, 1 the tip). L + W stays under 1.15: every stroke is found. */
vec3 slStrokes(vec2 uv, vec2 d, float L, float W, float jit, float taper, float dens, float px, float seed, out vec3 under) {
  vec2 i = floor(uv), f = fract(uv);
  float top = -1.0, cov = 0.0, id = 0.0, al = 0.0, top2 = -1.0;
  under = vec3(0.0);
  for (int a = -1; a <= 1; a++) for (int b = -1; b <= 1; b++) {
    vec2 g = vec2(float(a), float(b));
    if (slHash(vec3(i + g, seed + 0.5)) > dens) continue;
    vec3 h = slHash3(vec3(i + g, seed));
    vec2 r = f - (g + 0.15 + 0.7 * h.xy), dd = slRot(d, (h.z - 0.5) * jit);
    float len = L * (0.65 + 0.35 * fract(h.x * 7.31));
    float s = dot(r, dd), t = dot(r, slPerp(dd)), u = clamp(s / len, 0.0, 1.0);
    float e = length(vec2(s - u * len, t)) - W * (1.0 - taper * u);
    float cv = 1.0 - smoothstep(-px, px, e);
    if (cv > 0.002) {
      vec3 me = vec3(cv, fract(h.z * 9.17 + h.x), u);
      if (h.y > top) { if (top >= 0.0) { top2 = top; under = vec3(cov, id, al); } top = h.y; cov = me.x; id = me.y; al = me.z; }
      else if (h.y > top2) { top2 = h.y; under = me; }
    }
  }
  return vec3(cov, id, al);
}
/** strokes on cells ca long along d (constant in the plane: a frame that turned with the ground would swing the far
 *  coordinates) and cc across it */
vec3 slStrokesD(vec2 uv, vec2 d, float ca, float cc, float L, float W, float jit, float taper, float dens, float px, float seed, vec2 shift, out vec3 under) {
  vec2 q = vec2(dot(uv, d) / ca, dot(uv, slPerp(d)) / cc) + shift;
  return slStrokes(q, vec2(1.0, 0.0), L, W, jit, taper, dens, max(px / min(ca, cc), 0.02), seed, under);
}
/** a grass blade's colour: its own value and hue, its tip lighter, straw where it is dry */
vec3 slBlade(vec3 s, SlCtx x) {
  vec3 col = vec3((0.8 + 0.3 * s.y) * (0.66 + 0.62 * s.z)) * mix(vec3(1.04, 1.02, 0.9), vec3(0.93, 1.01, 1.03), fract(s.y * 3.7));
  if (x.on > 0.5 && fract(s.y * 5.13) < x.dry * 0.7) col *= vec3(1.16, 1.06, 0.7);
  return col;
}
/** D: each material's marks, laid over its field — a multiplier on the colour, its height into h */
vec3 slStrokeMarks(int m, SlCtx x, vec2 uv, vec2 wind, vec2 down, vec2 lean, vec2 cont, inout float h) {
  vec3 g = vec3(1.0), u;
  if (false) {}
#ifdef SL_M0
  else if (m == 0) {          // grass: blades (four layers, the ground between them in their shade) leaning with the wind and down the slope; fewer where dry or steep
    float cell = 0.22, k = slKeep(cell * 0.8, x.px), pc = max(x.px / cell, 0.02);
    float dens = x.on > 0.5 ? mix(1.0, 0.5, x.dry) * (1.0 - 0.5 * smoothstep(0.3, 0.6, x.slope)) : 0.9;
    g *= mix(1.0, 0.88, k);
    for (int l = 0; l < 4; l++) {
      vec3 s = slStrokes(uv / cell + float(l) * vec2(0.5, 0.33), lean, 1.0, 0.1, 0.7, 0.95, dens, pc, 31.0 + float(l), u);
      g = mix(g, slBlade(u, x), u.x * k); g = mix(g, slBlade(s, x), s.x * k);
    }
  }
#endif
#ifdef SL_M1
  else if (m == 1) {   // rock: broad flat chips (fractured planes, each its own value), cracks down the dip
    float cell = 1.1, k = slKeep(cell * 0.5, x.px);
    vec3 s = slStrokes(uv / cell, cont, 0.75, 0.3, 3.0, 0.1, 0.8, max(x.px / cell, 0.02), 41.0, u);
    g = mix(g, vec3(0.86 + 0.28 * u.y), u.x * k); g = mix(g, vec3(0.86 + 0.28 * s.y), s.x * k);
    float c2 = 1.6, k2 = slKeep(c2 * 0.3, x.px);
    vec3 cr = slStrokes(uv / c2, down, 1.0, 0.03, 0.7, 0.3, 0.55, max(x.px / c2, 0.02), 42.0, u);
    g *= 1.0 - 0.4 * max(cr.x, u.x) * k2;
  }
#endif
#ifdef SL_M2
  else if (m == 2) {   // sand: ripple crests across the wind (cells 2 people along a crest, 0.4 apart), each with its lee's shadow
    vec2 crest = slPerp(wind);
    float k = slKeep(0.25, x.px), amp = x.on > 0.5 ? 1.0 - smoothstep(0.2, 0.45, x.slope) : 1.0;
    vec3 sh = slStrokesD(uv, crest, 2.0, 0.4, 1.0, 0.14, 0.15, 0.6, 0.92, x.px, 51.0, vec2(0.0, 0.28), u);
    g = mix(g, vec3(0.88), max(sh.x, u.x) * k * amp);
    vec3 s = slStrokesD(uv, crest, 2.0, 0.4, 1.0, 0.07, 0.15, 0.6, 0.92, x.px, 51.0, vec2(0.0), u);
    g = mix(g, vec3(1.1), max(s.x, u.x) * k * amp);
  }
#endif
#ifdef SL_M3
  else if (m == 3) {   // snow: scour streaks along the wind where it is exposed, the soft crests of drifts across it
    float expo = x.on > 0.5 ? clamp(0.35 - 0.5 * x.cav + 0.5 * max(x.alt, 0.0), 0.0, 1.0) : 0.4;
    float k = slKeep(0.4, x.px);
    vec3 s = slStrokesD(uv, wind, 3.0, 0.5, 1.0, 0.08, 0.1, 0.6, expo, x.px, 61.0, vec2(0.0), u);
    g = mix(g, vec3(0.88, 0.93, 1.03), max(s.x, u.x) * k);
    float k2 = slKeep(3.0, x.px);
    vec3 d2 = slStrokesD(uv, slPerp(wind), 6.0, 3.0, 1.0, 0.22, 0.2, 0.5, 0.7, x.px, 62.0, vec2(0.0), u);
    g = mix(g, vec3(1.05), max(d2.x, u.x) * k2 * 0.6);
  }
#endif
#ifdef SL_M4
  else if (m == 4) {   // ice: white cracks
    float c1 = 1.5, k = slKeep(c1 * 0.3, x.px);
    vec3 s = slStrokes(uv / c1, wind, 1.0, 0.025, 2.5, 0.0, 0.8, max(x.px / c1, 0.02), 71.0, u);
    g = mix(g, vec3(1.25), max(s.x, u.x) * k);
  }
#endif
#ifdef SL_M5
  else if (m == 5) {   // mud: ruts down the fall line
    float c1 = 0.7, k = slKeep(c1 * 0.4, x.px);
    vec3 s = slStrokes(uv / c1, down, 1.0, 0.1, 0.35, 0.4, 0.6, max(x.px / c1, 0.02), 81.0, u);
    g = mix(g, vec3(0.76), max(s.x, u.x) * k);
  }
#endif
#ifdef SL_M6
  else if (m == 6) {   // ash: flecks
    float c1 = 0.22, k = slKeep(c1 * 0.5, x.px);
    vec3 s = slStrokes(uv / c1, wind, 0.35, 0.14, 6.28, 0.2, 0.9, max(x.px / c1, 0.02), 91.0, u);
    g = mix(g, vec3(0.8 + 0.4 * u.y), u.x * k); g = mix(g, vec3(0.8 + 0.4 * s.y), s.x * k);
  }
#endif
#ifdef SL_M7
  else if (m == 7) {     // salt: pressure ridges
    float c1 = 0.6, k = slKeep(c1 * 0.4, x.px);
    vec3 s = slStrokes(uv / c1, wind, 1.0, 0.06, 6.28, 0.3, 0.8, max(x.px / c1, 0.02), 101.0, u);
    g = mix(g, vec3(1.2), max(s.x, u.x) * k);
  }
#endif
  return g;
}

/** E and round 3: a painted tile (R its value, G warm/cool, B its height) — layer L (its slot in the world's array: uLIdx), by explicit gradients
 *  (the sample sits in a branch) */
vec4 slTex(int L, vec2 uv, vec2 gx, vec2 gy) { return textureGrad(uTiles, vec3(uv, uLIdx[L]), gx, gy); }
/** E's tessellation: hex tiling (Mikkelsen 2022) — the plane cut in hexes about a tile across; each hex's corner takes the tile
 *  shifted by its own hash and turned to the field's direction (± jit); the three nearest blended by the triangle's weights
 *  sharpened, the taller mark winning (a height blend), so neither the tile's repeat nor the hexes show */
vec3 slHex(int L, vec2 uv, vec2 gx, vec2 gy, vec2 d, float jit) {
  vec2 st = uv * 1.3;
  vec2 sk = vec2(st.x - 0.57735027 * st.y, 1.15470054 * st.y), base = floor(sk);
  vec3 t = vec3(fract(sk), 0.0); t.z = 1.0 - t.x - t.y;
  float s = step(0.0, -t.z), s2 = 2.0 * s - 1.0;
  vec3 w = vec3(-t.z * s2, s - t.y * s2, s - t.x * s2);
  vec3 acc = vec3(0.0); float ws = 0.0;
  for (int k = 0; k < 3; k++) {
    vec2 v = base + (k == 0 ? vec2(s, s) : k == 1 ? vec2(s, 1.0 - s) : vec2(1.0 - s, s));
    vec3 h = slHash3(vec3(v, 5.0 + float(L)));
    vec2 dd = slRot(d, (h.z - 0.5) * jit), pp = slPerp(dd);
    // (turned about the hex's corner — its point in uv — so the turn moves nothing far off; then shifted by its hash)
    vec2 vc = vec2(v.x + 0.5 * v.y, v.y / 1.15470054) / 1.3, r = uv - vc;
    vec3 tx = slTex(L, vec2(dot(r, dd), dot(r, pp)) + h.xy * 7.0, vec2(dot(gx, dd), dot(gx, pp)), vec2(dot(gy, dd), dot(gy, pp))).rgb;
    float wk = pow(w[k], 4.0) * (0.25 + tx.b);
    acc += wk * tx; ws += wk;
  }
  return acc / max(ws, 1e-5);
}
/** tile layer L laid by hex tiling along d (± jit), its value about the palette's colour (the tile's mean leaves it as it is) */
vec3 slTileL(int L, vec2 uv, vec2 gx, vec2 gy, vec2 d, float jit, inout float h) {
  float size = uLSize[L];
  vec3 t = slHex(L, uv / size, gx / size, gy / size, d, jit);
  float v = (0.55 + 0.9 * t.r) / (0.55 + 0.9 * uLMean[L]), tn = (t.g - 0.5) * 2.0;
  vec3 tint = tn > 0.0 ? mix(vec3(1.0), vec3(1.14, 1.05, 0.78), tn) : mix(vec3(1.0), vec3(0.88, 0.97, 1.1), -tn);
  h = mix(h, t.b, 0.8);
  return v * tint;
}
vec3 slTileMarks(int m, SlCtx x, vec2 uv, vec2 gx, vec2 gy, vec2 d, inout float h) {
  float jit = m == 0 ? 0.6 : m == 1 ? 0.5 : m == 2 ? 0.3 : m == 3 ? 0.2 : m == 4 ? 0.4 : m == 5 ? 0.5 : 6.28;
  return slTileL(m, uv, gx, gy, d, jit, h);
}

// ---- round 3 (371): a mark per surface, the method following the mark ----

/** rock's beds: level lines in the rock (the radius, warped), broken along their length, each bed its own value, a dark
 *  joint at its foot and a lit lip at its top — drawn where the rock stands steep */
vec3 slBeds(SlCtx x, inout float h) {
  float r = length(x.p) / 0.55 + 1.3 * slNoise(x.p / 7.0) + 0.25 * slNoise(x.p / 1.4);
  float f = fract(r), k = slKeep(0.55, x.px), id = slHash(vec3(floor(r), 3.0, 7.0));
  float brk = smoothstep(0.32, 0.5, slNoise(x.p / 2.4 + 3.1));
  float joint = (1.0 - smoothstep(0.0, 0.09, f)) * brk, lip = smoothstep(0.78, 0.97, f) * brk;
  h = mix(h, f * 0.7, 0.8);
  return vec3((0.9 + 0.22 * id * slKeep(0.3, x.px)) * (1.0 - 0.38 * joint * k) * (1.0 + 0.14 * lip * k));
}
/** ice's glints: a hashed tilt per small cell, and a cell glints where its tilt mirrors the sun into the eye (moves as you do) */
float slGlints(SlCtx x, vec2 uv) {
  float cell = 0.3;
  vec2 q = uv / cell, i = floor(q), f = fract(q);
  vec3 hs = slHash3(vec3(i, 151.0));
  vec3 n = normalize(x.up + 1.1 * (slHash3(vec3(i, 152.0)) - 0.5));
  float spec = pow(max(dot(n, normalize(uSunW + x.view)), 0.0), 600.0);
  float r = max(0.06, 1.4 * x.px / cell);
  return spec * (1.0 - smoothstep(r * 0.4, r, length(f - (0.2 + 0.6 * hs.xy)))) * (1.0 - smoothstep(0.02, 0.1, x.px));
}
/** Round 3: material m's mark — compiled per world: SL_MK(m) its mark, and only the marks this world's materials take
 *  (SL_K<id>); strokes or a tile, as the mark asks. The marks the picks reach (DECISIONS 375). */
vec3 slMarks3(int m, SlCtx x, vec2 uv, vec2 gx, vec2 gy, vec2 wind, vec2 down, vec2 lean, vec2 cont, inout float h) {
  int k = int(SL_MK(m) + 0.5);
#ifdef SL_K3
  if (k == 3) return slTileL(9, uv, gx, gy, lean, 1.1, h);          // grass: hatch, each hex its own angle
#endif
#ifdef SL_K4
  if (k == 4) return slTileL(10, uv, gx, gy, wind, 0.0, h);         // dirt: clods (lit as painted: shifted, not turned)
#endif
#ifdef SL_K10
  if (k == 10) return slStrokeMarks(1, x, uv, wind, down, lean, cont, h);   // rock: chips and cracks (strokes)
#endif
#ifdef SL_K12
  if (k == 12) {                                                     // rock: gravel on the flat, beds where steep
    vec3 fl = slTileL(11, uv, gx, gy, wind, 0.0, h);
    float st = smoothstep(0.12, 0.3, x.slope), hb = h;
    vec3 bd = st > 0.0 ? slBeds(x, hb) : vec3(1.0);
    h = mix(h, hb, st);
    return mix(fl, bd, st);
  }
#endif
#ifdef SL_K20
  if (k == 20) return slTileL(2, uv, gx, gy, wind, 0.3, h);         // sand: E's ripples
#endif
#ifdef SL_K21
  if (k == 21) return slStrokeMarks(2, x, uv, wind, down, lean, cont, h);   // sand: D's crests
#endif
#ifdef SL_K31
  if (k == 31) return slStrokeMarks(3, x, uv, wind, down, lean, cont, h);   // snow: D's scour and drifts
#endif
#ifdef SL_K32
  if (k == 32) return slTileL(3, uv, gx, gy, wind, 0.2, h);         // snow: E's painted drifts and scour
#endif
#ifdef SL_K41
  if (k == 41) return slTileL(13, uv, gx, gy, wind, 6.28, h) * (1.0 + 2.5 * slGlints(x, uv));   // ice: plates (and glints)
#endif
#ifdef SL_K42
  if (k == 42) return slTileL(4, uv, gx, gy, wind, 0.4, h);         // ice: E's painted streaks and cracks
#endif
#ifdef SL_K51
  if (k == 51) return slTileL(5, uv, gx, gy, down, 0.5, h);         // mud: E's ruts and puddles
#endif
#ifdef SL_K61
  if (k == 61) return slTileL(6, uv, gx, gy, wind, 6.28, h);        // ash: E's grain and drifts
#endif
#ifdef SL_K71
  if (k == 71) return slTileL(7, uv, gx, gy, wind, 6.28, h);        // salt: E's ridges
#endif
  return vec3(1.0);
}

/** The world's wind, a direction fixed in space (the sheet's; the weather's wind is a later step) */
const vec3 SL_WIND = vec3(0.6178, 0.2059, 0.7587);
/** One plane of the triplanar frame: the fields' directions put into it, the field, and the take's marks over it */
vec3 slPlane(int m, SlCtx x, vec2 uv, vec3 ax, vec3 ay, out float h) {
  vec2 wind = normalize(vec2(dot(SL_WIND, ax), dot(SL_WIND, ay)) + 1e-6), down = normalize(vec2(dot(x.down, ax), dot(x.down, ay)) + 1e-6);
  vec2 lean = normalize(vec2(dot(x.lean, ax), dot(x.lean, ay)) + 1e-6), cont = normalize(vec2(dot(x.contour, ax), dot(x.contour, ay)) + 1e-6);
#if SL_LOOK == 8
  vec3 g = slField(m, x, uv, wind, down, lean, m == 0 ? 0.0 : 0.2, h);
#else
  vec3 g = slField(m, x, uv, wind, down, lean, 0.35, h);
#endif
#if SL_LOOK == 5
  g *= slStrokeMarks(m, x, uv, wind, down, lean, cont, h);
#elif SL_LOOK == 6
  vec2 gx = vec2(dot(x.dpx, ax), dot(x.dpx, ay)), gy = vec2(dot(x.dpy, ax), dot(x.dpy, ay));
  g *= slTileMarks(m, x, uv, gx, gy, m == 0 ? lean : m == 1 ? cont : m == 5 ? down : wind, h);
#elif SL_LOOK == 8
  vec2 gx = vec2(dot(x.dpx, ax), dot(x.dpx, ay)), gy = vec2(dot(x.dpy, ax), dot(x.dpy, ay));
  g *= slMarks3(m, x, uv, gx, gy, wind, down, lean, cont, h);
#endif
  return g;
}
/** Triplanar on the planet's axes, the weights sharpened (the 8th power): one plane over most of the sphere, two in a band */
vec3 slTri(int m, SlCtx x, out float h) {
  vec3 w = pow(abs(x.up), vec3(8.0)); w /= w.x + w.y + w.z;
  vec3 acc = vec3(0.0); float hs = 0.0, ws = 0.0, hk;
  if (w.x > 0.03) { acc += w.x * slPlane(m, x, x.p.yz, vec3(0.0, 1.0, 0.0), vec3(0.0, 0.0, 1.0), hk); hs += w.x * hk; ws += w.x; }
  if (w.y > 0.03) { acc += w.y * slPlane(m, x, x.p.zx, vec3(0.0, 0.0, 1.0), vec3(1.0, 0.0, 0.0), hk); hs += w.y * hk; ws += w.y; }
  if (w.z > 0.03) { acc += w.z * slPlane(m, x, x.p.xy, vec3(1.0, 0.0, 0.0), vec3(0.0, 1.0, 0.0), hk); hs += w.z * hk; ws += w.z; }
  h = hs / ws;
  return acc / ws;
}
/** The rules' colour: the aspect (sun-facing warmer and drier, the shaded cooler), dry grass to straw and wet to lush, damp
 *  sand and ash darker, lichen on rock in damp hollows, and the heights paler and cooler */
void slRulesTint(inout vec3 c, int m, SlCtx x) {
  if (x.on < 0.5) return;
  c *= mix(vec3(1.0), x.aspect > 0.0 ? vec3(1.05, 1.01, 0.92) : vec3(0.94, 0.99, 1.06), abs(x.aspect));
  if (m == 0) {
    c = mix(c, c * vec3(1.2, 1.07, 0.66), 0.5 * x.dry);
    c = mix(c, c * vec3(0.84, 1.05, 0.8), 0.5 * (1.0 - x.dry) * x.moist);
  } else if (m == 2 || m == 6) c *= mix(1.0, 0.8, smoothstep(0.6, 0.9, x.moist));
  else if (m == 1) c = mix(c, c * vec3(0.86, 0.95, 0.8), 0.35 * smoothstep(0.55, 0.85, x.moist));
  else if (m == 3) c *= mix(1.0, 1.05, clamp(x.alt, 0.0, 1.0));
  c = mix(c, vec3(dot(c, vec3(0.3, 0.59, 0.11))) * vec3(0.97, 1.0, 1.05), 0.22 * clamp(x.alt, 0.0, 1.0));
}`;
